import Box from '@mui/material/Box';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { type Control, Controller, type FieldPath, type FieldValues } from 'react-hook-form';

import { InfoHint } from '@/components/atoms';

type Props<T extends FieldValues, TOut = T> = {
  control: Control<T, unknown, TOut>;
  name: FieldPath<T>;
  label: string;
  placeholder?: string;
  helperText?: string;
  /**
   * A sentence explaining the field, offered behind a hint button beside the
   * helper text. For the words a visitor needs *before* they can answer, use
   * `helperText`: a hint has to be opened, and an instruction that has to be
   * opened is an instruction some visitors never read.
   */
  help?: string;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  /**
   * `date` and `time` are here so a form can hand the question to the browser's
   * own picker instead of captioning a text box with the format it wants. A
   * native `date` input yields `YYYY-MM-DD`, which is what the enquiry schemas
   * already validate, so nothing downstream changes.
   */
  type?: 'text' | 'email' | 'tel' | 'date' | 'time';
  autoComplete?: string;
  inputMode?: TextFieldProps['inputMode'];
};

/** Text input bound to React Hook Form, with errors announced in helper text. */
export function TextInputField<T extends FieldValues, TOut = T>({
  control,
  name,
  label,
  placeholder,
  helperText,
  help,
  required = false,
  multiline = false,
  rows,
  type = 'text',
  autoComplete,
  inputMode,
}: Props<T, TOut>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const message = fieldState.error?.message ?? helperText;
        /* A row, not a run of inline content: inline, the hint button is the
           last word of the helper text and drops onto a line of its own the
           moment that text wraps — which on a phone is almost every field,
           and reads as a stray icon rather than as help. Pinned to the
           field's trailing edge it keeps the row's height, stays beside the
           words it explains, and lines the hints up into one scannable rail
           down the form. */
        const helper =
          message || help ? (
            <Box
              component="span"
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Box component="span">{message}</Box>
              {help && <InfoHint label={label}>{help}</InfoHint>}
            </Box>
          ) : undefined;

        return (
          <TextField
            label={label}
            value={field.value ?? ''}
            name={field.name}
            onChange={field.onChange}
            onBlur={field.onBlur}
            inputRef={field.ref}
            type={type}
            required={required}
            multiline={multiline}
            {...(rows ? { rows, minRows: rows } : {})}
            {...(placeholder ? { placeholder } : {})}
            {...(autoComplete ? { autoComplete } : {})}
            {...(inputMode ? { inputMode } : {})}
            error={Boolean(fieldState.error)}
            helperText={helper}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        );
      }}
    />
  );
}
